import React, { useState, useRef, useEffect } from 'react'
import { Box, Card, Divider, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material'
import FormatColorTextIcon from '@mui/icons-material/FormatColorText'
import FontDownloadIcon from '@mui/icons-material/FontDownload'
import { useTranslate } from 'ra-core'
import { useTiptapEditor } from 'ra-input-rich-text'
import { grey, red, orange, yellow, green, blue, purple } from '@mui/material/colors'

const ColorType = {
  FONT: 'font',
  BACKGROUND: 'background',
}

export const ColorButtons = (props) => {
  const translate = useTranslate()
  const editor = useTiptapEditor()
  const [showColorChoiceDialog, setShowColorChoiceDialog] = useState(false)
  const [colorType, setColorType] = useState(ColorType.FONT)

  const colorLabel = translate('ra.tiptap.color', { _: 'Color' })
  const highlightLabel = translate('ra.tiptap.highlight', { _: 'Highlight' })

  const displayColorChoiceDialog = (colorType) => {
    setShowColorChoiceDialog(true)
    setColorType(colorType)
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <OutsideListener onClick={() => setShowColorChoiceDialog(false)}>
        <ToggleButtonGroup>
          <ToggleButton
            aria-label={colorLabel}
            title={colorLabel}
            {...props}
            disabled={!editor || !editor.isEditable}
            value="color"
            onClick={() => displayColorChoiceDialog(ColorType.FONT)}
          >
            <FormatColorTextIcon fontSize="inherit" />
          </ToggleButton>
          <ToggleButton
            aria-label={highlightLabel}
            title={highlightLabel}
            {...props}
            disabled={!editor || !editor.isEditable}
            value="highlight"
            onClick={() => displayColorChoiceDialog(ColorType.BACKGROUND)}
          >
            <FontDownloadIcon fontSize="inherit" />
          </ToggleButton>
        </ToggleButtonGroup>
        {showColorChoiceDialog && (
          <ColorChoiceDialog
            editor={editor}
            close={() => setShowColorChoiceDialog(false)}
            colorType={colorType}
          />
        )}
      </OutsideListener>
    </Box>
  )
}

const ColorChoiceDialog = ({ editor, close, colorType }) => {
  const theme = useTheme()
  const baseColors = [grey, red, orange, yellow, green, blue, purple]
  const customColors = ['#D537A8', '#89216C', '#F3F1F1', '#D5CEDE', '#4C82C2', '#241426']

  const shades = [900, 700, 500, 300, 100]

  const selectColor = (color) => {
    if (colorType === ColorType.FONT) {
      editor
        .chain()
        .focus()
        .setColor(color)
        .run()
    } else {
      editor
        .chain()
        .focus()
        .toggleHighlight({ color })
        .run()
    }
    close()
  }

  return (
    <Card
      sx={{
        position: 'absolute',
        top: 38,
        left: colorType === ColorType.FONT ? 0 : '50%',
        p: 1,
        border: `1px solid ${theme?.palette?.background?.default}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        zIndex: 1,
      }}
    >
      {shades.map((shade, line) => (
        <Box key={`shade-${shade}`} sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          {baseColors.map((color, row) => (
            <Box
              key={`color-${line * baseColors.length + row + 1}`}
              sx={{
                width: 16,
                height: 16,
                cursor: 'pointer',
                backgroundColor: color[shade],
              }}
              onClick={() => selectColor(color[shade])}
            ></Box>
          ))}
        </Box>
      ))}
      <Divider orientation="horizontal" flexItem />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          flexWrap: customColors.length > baseColors.length ? 'wrap' : 'nowrap',
        }}
      >
        {customColors.map((color, index) => (
          <Box
            key={`color-${index}`}
            sx={{
              width: 16,
              height: 16,
              cursor: 'pointer',
              backgroundColor: color,
            }}
            onClick={() => selectColor(color)}
          ></Box>
        ))}
      </Box>
    </Card>
  )
}

const OutsideListener = ({ className, onClick, children }) => {
  const wrapperRef = useRef(null)
  useOutsideListener(wrapperRef, onClick)

  return (
    <div className={className} ref={wrapperRef}>
      {children}
    </div>
  )
}

const useOutsideListener = (ref, onClick) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClick()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref, onClick])
}
